import type { CSSProperties } from 'react';

/**
 * Cosmos typography — ONE consume API.
 * SSOT sizes/colors: globals.css :root (--fs-* 6 steps, --type-*, --cosmos-*)
 *
 * Use `typeRole` only — same role = same size every page.
 */

/** Wireframe / lobby scaled size values (inline style) */
export const typo = {
  md: 'var(--wf-text-md)',
  base: 'var(--wf-text-base)',
  lg: 'var(--wf-text-lg)',
  xl: 'var(--wf-text-xl)',
  display: 'var(--wf-text-display)',
  /** Fixed 16px — form inputs (iOS zoom guard) */
  input: 'var(--fs-input)',
} as const;

const t = {
  header1: 'text-[length:var(--type-header1)]',
  header1Lg: 'text-[length:var(--type-header1-lg)]',
  header2: 'text-[length:var(--type-header2)]',
  body: 'text-[length:var(--type-body)]',
  caption: 'text-[length:var(--type-caption)]',
  label: 'text-[length:var(--type-label)]',
  nav: 'text-[length:var(--type-nav)]',
  brand: 'text-[length:var(--type-brand)]',
  button: 'text-[length:var(--type-button)]',
  chip: 'text-[length:var(--type-chip)]',
  money: 'text-[length:var(--type-money)]',
  moneyHero: 'text-[length:var(--type-money-hero)]',
  input: 'text-[length:var(--type-input)]',
} as const;

/** Fixed roles — prefer these everywhere */
export const typeRole = {
  /** Page title */
  header1: `${t.header1} sm:${t.header1Lg} font-display`,
  /** Section / modal title */
  header2: `${t.header2} font-display`,
  body: t.body,
  caption: t.caption,
  label: t.label,
  nav: t.nav,
  brand: t.brand,
  button: t.button,
  chip: t.chip,
  money: `${t.money} font-money`,
  moneyHero: `${t.moneyHero} font-money`,
  input: t.input,
} as const;

/** Fixed --fs-* steps when a raw step is required (badges, etc.) */
export const fsClass = {
  md: 'text-[length:var(--fs-md)]',
  base: 'text-[length:var(--fs-base)]',
  lg: 'text-[length:var(--fs-lg)]',
  input: 'text-[length:var(--fs-input)]',
  xl: 'text-[length:var(--fs-xl)]',
  display: 'text-[length:var(--fs-display)]',
  // legacy aliases → canonical 6
  xlPlus: 'text-[length:var(--fs-xl-plus)]',
  displaySm: 'text-[length:var(--fs-display-sm)]',
  displayLg: 'text-[length:var(--fs-display-lg)]',
  displayXl: 'text-[length:var(--fs-display-xl)]',
  display2xl: 'text-[length:var(--fs-display-2xl)]',
} as const;

/** Cosmos utility classes (globals.css) */
export const typeClass = {
  md: 'cosmos-type-md',
  base: 'cosmos-type-base',
  lg: 'cosmos-type-lg',
  xl: 'cosmos-type-xl',
  display: 'cosmos-type-display',
} as const;

/** Style objects — page titles → header1; sections → header2 */
export const wfPageTitleStyle: CSSProperties = { fontSize: 'var(--type-header1)' };
export const wfTitleStyle: CSSProperties = { fontSize: 'var(--type-header1)' };
export const wfSectionTitleStyle: CSSProperties = { fontSize: 'var(--type-header2)' };
export const wfSubtitleStyle: CSSProperties = { fontSize: 'var(--type-body)' };
export const wfLabelStyle: CSSProperties = { fontSize: 'var(--type-label)' };
export const wfRowLabelStyle: CSSProperties = { fontSize: 'var(--type-body)' };
export const wfHintStyle: CSSProperties = { fontSize: 'var(--type-caption)' };
export const wfBodyStyle: CSSProperties = { fontSize: 'var(--type-body)' };

export const wfMutedStyle: CSSProperties = { fontSize: 'var(--wf-muted)' };
export const wfHeroStyle: CSSProperties = { fontSize: 'var(--wf-hero)' };
export const wfContinueStyle: CSSProperties = { fontSize: 'var(--wf-continue)' };
export const wfGamesNameStyle: CSSProperties = { fontSize: 'var(--wf-games-name)' };
export const wfGamesSubStyle: CSSProperties = { fontSize: 'var(--wf-games-sub)' };
export const wfGamesTabStyle: CSSProperties = { fontSize: 'var(--wf-games-tab)' };
export const wfGamesDdiStyle: CSSProperties = { fontSize: 'var(--wf-games-ddi)' };
export const wfCardTitleStyle: CSSProperties = { fontSize: 'var(--wf-card-title)' };
export const wfCardSubStyle: CSSProperties = { fontSize: 'var(--wf-card-sub)' };
export const wfActDateStyle: CSSProperties = { fontSize: 'var(--wf-act-date)' };
export const wfPillStyle: CSSProperties = { fontSize: 'var(--wf-pill)' };
export const wfNavStyle: CSSProperties = { fontSize: 'var(--wf-nav)' };
export const wfChipFontStyle: CSSProperties = { fontSize: 'var(--wf-chip)' };
export const wfInpFontStyle: CSSProperties = { fontSize: 'var(--wf-inp)' };
export const wfMdlLinkStyle: CSSProperties = { fontSize: 'var(--wf-mdl-link)' };
export const wfHdActionStyle: CSSProperties = { fontSize: 'var(--wf-hd-action)' };
export const wfBrandStyle: CSSProperties = { fontSize: 'var(--wf-brand)' };
export const wfGiLabelStyle: CSSProperties = { fontSize: 'var(--wf-gi-label)' };
export const wfTickStyle: CSSProperties = { fontSize: 'var(--wf-tick)' };
